import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatLogTimestamp } from '../../utils/dateUtils';
import './InstanceLogs.css';

interface InstanceLogsProps {
  kgId: string;
  versionId: string;
  instanceId: string;
  onClose: () => void;
}

interface LogEvent {
  timestamp: number;
  message: string;
  ingestionTime: number;
}

const InstanceLogs: React.FC<InstanceLogsProps> = ({ kgId, versionId, instanceId, onClose }) => {
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextForwardToken, setNextForwardToken] = useState<string | null>(null);
  const [nextBackwardToken, setNextBackwardToken] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showTopArrow, setShowTopArrow] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadLogs();
  }, [kgId, versionId, instanceId]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadMoreLogs();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, nextForwardToken]);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    const container = logsContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    // Show top arrow only when at the very top and there are previous logs
    setShowTopArrow(isAtTop && !!nextBackwardToken);

    // Show bottom arrow only when at the very bottom and there are more logs (and not auto-refreshing)
    setShowBottomArrow(isAtBottom && !!nextForwardToken && !autoRefresh);
  };

  useEffect(() => {
    const container = logsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [nextForwardToken, nextBackwardToken, autoRefresh]);

  const loadLogs = async (token?: string) => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, token);

      if (response) {
        const events = response.events || [];
        setLogs(events);
        setNextForwardToken(response.nextForwardToken || null);
        setNextBackwardToken(response.nextBackwardToken || null);

        // Scroll to bottom after loading initial logs
        setTimeout(() => {
          scrollToBottom();
          handleScroll();
        }, 100);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load logs'));
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLogs = async () => {
    if (!nextForwardToken) return;

    try {
      const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, nextForwardToken);

      if (response) {
        const events = response.events || [];
        if (events.length > 0) {
          setLogs(prev => [...prev, ...events]);
          setNextForwardToken(response.nextForwardToken || null);
          setNextBackwardToken(response.nextBackwardToken || null);

          // Auto-scroll to bottom when new logs arrive
          setTimeout(() => {
            if (autoRefresh) {
              scrollToBottom();
            }
            handleScroll();
          }, 100);
        }
      }
    } catch (err: any) {
      console.error('Error loading more logs:', err);
    }
  };

  const loadPreviousLogs = async () => {
    if (!nextBackwardToken) return;

    try {
      const container = logsContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;

      const response: any = await apiService.getInstanceLogs(kgId, versionId, instanceId, nextBackwardToken);

      if (response) {
        const events = response.events || [];
        setLogs(prev => [...events, ...prev]);
        setNextForwardToken(response.nextForwardToken || null);
        setNextBackwardToken(response.nextBackwardToken || null);

        // Maintain scroll position after prepending logs
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
            handleScroll();
          }
        }, 0);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load previous logs'));
      console.error('Error loading previous logs:', err);
    }
  };

  const filteredLogs = logs.filter((log) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      log.message.toLowerCase().includes(searchLower) ||
      formatLogTimestamp(log.timestamp).toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="instance-logs-modal">
      <div className="logs-modal-content">
        <div className="logs-header">
          <h2>Instance Logs</h2>
          <div className="logs-header-actions">
            <input
              type="text"
              className="search-input-logs"
              placeholder="Search logs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh (5s)</span>
            </label>
            <button className="btn-icon" onClick={() => loadMoreLogs()} title="Load Next Page">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </button>
            <button className="btn-icon" onClick={onClose} title="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div className="logs-info">
          <span className="info-text instance-id-full">Instance ID: {instanceId}</span>
          <span className="info-text">
            {searchTerm ? `${filteredLogs.length} of ${logs.length} events` : `${logs.length} events`}
          </span>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showTopArrow && (
          <div className="scroll-arrow scroll-arrow-top" onClick={loadPreviousLogs}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
            </svg>
            <span>Load Previous Logs</span>
          </div>
        )}

        <div className="logs-container" ref={logsContainerRef}>
          {loading && logs.length === 0 ? (
            <div className="page-loader"><div className="page-spinner" /></div>
          ) : logs.length === 0 ? (
            <div className="logs-empty">No logs available</div>
          ) : filteredLogs.length === 0 ? (
            <div className="logs-empty">No logs match your search</div>
          ) : (
            <div className="logs-list">
              {filteredLogs.map((log, index) => (
                <div key={`${log.timestamp}-${index}`} className="log-entry">
                  <span className="log-timestamp">{formatLogTimestamp(log.timestamp)}</span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>

        {showBottomArrow && (
          <div className="scroll-arrow scroll-arrow-bottom" onClick={loadMoreLogs}>
            <span>Load More Logs</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstanceLogs;
